import {isEqual, sortBy} from 'lodash';
import React, {FC, useCallback, useEffect, useState} from 'react';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {OkCancelDialog} from '../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import p2TabsImage from '../../../../assets/images/p2Tabs.svg';
import {InboxesFiltersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {InboxesConversationsParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {areParametersCellsEqual} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {ComplexTooltipContent} from '../../../common/tooltips/complexTooltipContent';

/*
 * Props.
 */

interface TeamTabsDialogComponentProps {
  parameters: InboxesConversationsParameters;
  markTeamTabsAlertAsSeen: () => void;
  recordUnassignedAssignedSwitch: () => void;
  switchToTeammateTabs: () => void;
  hasSeenTeamTabsAlert: boolean;
  unassignedAssignedSwitchCount: number;
}

/*
 * Component.
 */

export const TeamTabsDialog: FC<TeamTabsDialogComponentProps> = (props) => {
  const localize = useLocalize();
  const [shouldShowAlert, setShouldShowAlert] = useState(false);
  const previousProps = usePrevious(props);

  const onRequestCloseClick = useCallback(() => {
    setShouldShowAlert(false);
    props.markTeamTabsAlertAsSeen();
  }, [props]);
  const switchTabs = useCallback(() => {
    onRequestCloseClick();
    props.switchToTeammateTabs();
  }, [onRequestCloseClick, props]);

  useEffect(() => {
    if (!previousProps) {
      return;
    }

    // If the user already saw the alert, nothing to do
    if (props.hasSeenTeamTabsAlert) {
      return;
    }

    const newParameters = props.parameters;
    const previousParameters = previousProps.parameters;

    // If we changed route completely, nothing to do.
    if (areParametersCellsEqual(newParameters, previousParameters)) {
      return;
    }

    const newFilters = sortBy(newParameters.filters);
    const previousFilters = sortBy(previousParameters.filters);
    const assignFilters = [InboxesFiltersEnum.ASSIGNED, InboxesFiltersEnum.UNASSIGNED];
    const previousHasOneAssignFilter =
      previousFilters.length === 1 && assignFilters.includes(previousFilters[0]);
    const newHasBothAssignFilter = isEqual(assignFilters, newFilters);

    const didSwitchBetweenAssignedAndUnassigned =
      newFilters.length === 1 &&
      previousFilters.length === 1 &&
      !isEqual(newFilters, previousFilters) &&
      [...previousFilters, ...newFilters].every((f) => assignFilters.includes(f));

    const didCombineUnassignedAndAssigned = previousHasOneAssignFilter && newHasBothAssignFilter;

    if (didCombineUnassignedAndAssigned || props.unassignedAssignedSwitchCount > 5) {
      setShouldShowAlert(true);
      props.markTeamTabsAlertAsSeen();
      return;
    }

    if (didSwitchBetweenAssignedAndUnassigned) {
      props.recordUnassignedAssignedSwitch();
    }
  }, [previousProps, props]);

  if (!shouldShowAlert) {
    return null;
  }

  const renderTooltip = () => (
    <ComplexTooltipContent
      maxWidth={246}
      title={<Trans id="teamtabsdialog-tooltip-title">Open tab</Trans>}
      body={
        <Trans id="teamtabsdialog-tooltip-body">
          Combine your unassigned and assigned conversations into one view.
        </Trans>
      }
      imageUrl={p2TabsImage}
      imageDescription={localize(
        defineMessage({
          id: 'teamtabsdialog-tooltip-imagealt',
          message: 'filters with Open, Archived and Snoozed',
        }),
      )}
    />
  );

  return (
    <OkCancelDialog
      title={<Trans id="teamtabsdialog-title">Change shared inbox tabs preference</Trans>}
      okButtonLabel={<Trans id="teamtabsdialog-confirm">Change preference</Trans>}
      okButtonVariant={ButtonVariantsEnum.SECONDARY}
      onRequestClose={onRequestCloseClick}
      onOkClick={switchTabs}
      renderTooltip={renderTooltip}
    >
      <Trans id="teamtabsdialog-content">
        Combine <strong>Unassigned</strong> and <strong>Assigned</strong> into one <strong>Open</strong> tab
        to view these conversations together. This does not affect the display of any other teammate.
        <br />
        <br />
        You can change this later in <em>Settings</em> &gt; <em>My Preferences</em>.
      </Trans>
    </OkCancelDialog>
  );
};
